import { BUSINESS, PLUS, SCALE } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  SITE_BAR_LINK,
  SUBSCRIPTION_COST_SUMMARY_BUSINESS,
} from '@hotjar/webapp/domains/revenue/constants/upsellReferrers';
import FilterErrorIllustration from '@hotjar/shared-assets/images/error-capturing.png';
import TrendsSplashIllustration from '@hotjar/shared-assets/images/trends-splash.svg';
import FunnelsSplashIllustration from '@hotjar/shared-assets/images/funnels-splash.svg';

import { PlanComparisonFeatureGate, TrafficRecommendationFeatureGate } from './types';
import type {
  PlanComparisonFeatureGateDetails,
  PlanComparisonReferrer,
  TrafficRecommendationReferrer,
} from './types';
import CustomReactionScalesIllustration from './images/custom-reaction-scales.png';
import FiltersIllustration from './images/filters.svg';
import FilterNpsIllustration from './images/filter-nps.svg';
import FilterUTurnIllustration from './images/filter-u-turn.svg';
import ObserveGoogleOptimizeIllustration from './images/google-optimize.png';
import IncomingFeedbackIllustration from './images/incoming-feedback.svg';
import ObserveOptimizelyIllustration from './images/optimizely.png';
import PageTargetingIllustration from './images/page-targeting.svg';
import PercentageTargetingIllustration from './images/percentage-targeting.svg';
import RemoveBrandingIllustration from './images/remove-branding.png';
import ObserveSlackIllustration from './images/slack.png';
import ObserveMsTeamsIllustration from './images/ms-teams.png';
import WebhooksIllustration from './images/webhooks.png';
import SurveyCustomizationIllustration from './images/survey-customization.png';
import SurveysIllustration from './images/surveys.svg';
import ObserveZapierIllustration from './images/zapier.png';
import TrafficIllustration from './images/traffic.svg';
import SurveyQuestionImagesIllustration from './images/survey-question-images.svg';
import SurveyAppearanceLogoIllustration from './images/survey-appearance-logo.svg';
import PublicApiIllustration from './images/responses-api-image.svg';
import UnbounceIllustration from './images/unbounce.png';

export const MODAL_NAME = 'Plan comparison modal';

export const HJ_PRICING_PAGE = 'https://www.hotjar.com/pricing/';

/**
 * Feature gate names mapped by upsell referrer. We need this, because the referrer
 * describes just what circumstance triggered the upsell, and not necessarily what feature
 * needs to be upselled.
 *
 * Note: You probably don't need to access this object in component-code. Please see the
 * `usePlanComparisonFeatureGate` hook.
 */
export const planComparisonFeatureGatesByReferrer: Record<
  PlanComparisonReferrer,
  PlanComparisonFeatureGate
> = {
  // Observe
  'settings.integrations_google_optimize': PlanComparisonFeatureGate.GoogleOptimize,
  'settings.integrations_optimizely': PlanComparisonFeatureGate.Optimizely,
  'settings.integrations_slack': PlanComparisonFeatureGate.ObserveSlack,
  'settings.integrations_teams': PlanComparisonFeatureGate.ObserveMsTeams,
  'settings.integrations_webhooks': PlanComparisonFeatureGate.ObserveWebhooks,
  'analytics_product.integrations_slack': PlanComparisonFeatureGate.ObserveSlack,
  'analytics_product.integrations_zapier': PlanComparisonFeatureGate.ObserveZapier,
  'analytics_product.integrations_teams': PlanComparisonFeatureGate.ObserveMsTeams,
  'analytics_product.integrations_webhooks': PlanComparisonFeatureGate.ObserveWebhooks,
  'site.settings_targeting': PlanComparisonFeatureGate.PageTargeting,
  'site.console_tracking': PlanComparisonFeatureGate.ErrorConsole,
  trends_splash_screen_upgrade_link: PlanComparisonFeatureGate.Trends,
  funnels_splash_screen_upgrade_link: PlanComparisonFeatureGate.Funnels,
  'recordings.heatmaps_error_widget.empty': PlanComparisonFeatureGate.ErrorConsole,
  'recordings.error_console.empty': PlanComparisonFeatureGate.ErrorConsole,
  'recordings.session_info.error_widget.empty': PlanComparisonFeatureGate.ErrorConsole,
  'recordings.recommended_error_widget.empty': PlanComparisonFeatureGate.ErrorConsole,

  // Ask
  'settings.integrations_zapier': PlanComparisonFeatureGate.AskZapier,
  'feedback.appearance.accent_color': PlanComparisonFeatureGate.SurveyCustomization,
  'feedback.appearance.widget_label': PlanComparisonFeatureGate.SurveyCustomization,
  'feedback.branding.toggle': PlanComparisonFeatureGate.RemoveHotjarBranding,
  'feedback.emotion_style_choices': PlanComparisonFeatureGate.CustomReactionScales,
  'feedback.limit.reached': PlanComparisonFeatureGate.IncomingFeedback,
  'feedback.targeting.attributes': PlanComparisonFeatureGate.UserAttributes,
  'feedback.targeting.cap': PlanComparisonFeatureGate.PercentageTargeting,
  'feedback.targeting.triggers': PlanComparisonFeatureGate.AskEventsApi,
  'polls.branding.toggle': PlanComparisonFeatureGate.RemoveHotjarBranding,
  'polls.go-unlimited': PlanComparisonFeatureGate.Surveys,
  'polls.limit.reached': PlanComparisonFeatureGate.Surveys,
  'polls.targeting.attributes': PlanComparisonFeatureGate.UserAttributes,
  'polls.targeting.cap': PlanComparisonFeatureGate.PercentageTargeting,
  'polls.targeting.triggers': PlanComparisonFeatureGate.AskEventsApi,
  'survey.appearance.colors': PlanComparisonFeatureGate.SurveyCustomization,
  'survey.appearance.logo': PlanComparisonFeatureGate.SurveyAppearanceLogo,
  'survey.emotion_style_choices': PlanComparisonFeatureGate.CustomReactionScales,
  'surveys.performance': PlanComparisonFeatureGate.SurveyPerformance,
  'survey.question.images': PlanComparisonFeatureGate.SurveyQuestionImages,
  'user-attributes.upgrade': PlanComparisonFeatureGate.UserAttributes,
  'feedback_product.integrations_slack': PlanComparisonFeatureGate.AskSlack,
  'feedback_product.integrations_zapier': PlanComparisonFeatureGate.AskZapier,
  'feedback_product.integrations_teams': PlanComparisonFeatureGate.AskMsTeams,
  'feedback_product.integrations_webhooks': PlanComparisonFeatureGate.AskWebhooks,
  'overview.upsell_banner.ask': PlanComparisonFeatureGate.Surveys,
  public_api: PlanComparisonFeatureGate.PublicAPI,

  // Observe filters
  'analytics_product.filters.google_optimize': PlanComparisonFeatureGate.GoogleOptimize,
  'analytics_product.filters.optimizely': PlanComparisonFeatureGate.Optimizely,
  'analytics_product.filters.unbounce': PlanComparisonFeatureGate.Unbounce,
  'analytics_product.filters.incoming_feedback': PlanComparisonFeatureGate.FilterIncomingFeedback,
  'analytics_product.filters.nps': PlanComparisonFeatureGate.FilterNps,
  'analytics_product.filters.referrer_url': PlanComparisonFeatureGate.FilterReferrerUrl,
  'analytics_product.filters.rage_click': PlanComparisonFeatureGate.FilterRageClicks,
  'analytics_product.filters.user_attribute': PlanComparisonFeatureGate.FilterUserAttributes,
  'analytics_product.filters.u_turn': PlanComparisonFeatureGate.FilterUTurn,
  'analytics_product.filters.favorite': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.visited_url': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.landing_url': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.exit_url': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.new_returning_user': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.clicked': PlanComparisonFeatureGate.FilterClicked,
  'analytics_product.filters.country': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.session_duration': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.page_count': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.tag': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.events': PlanComparisonFeatureGate.ObserveEventsApi,
  'feedback_product.response_limit_50_percent': PlanComparisonFeatureGate.AskResponseLimit,
  'feedback_product.response_limit_100_percent': PlanComparisonFeatureGate.AskResponseLimit,
  'analytics_product.filters.user_id': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.team_comment': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.device': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.browser': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.os': PlanComparisonFeatureGate.ObserveAdvancedFilters,
  'analytics_product.filters.menu_footer': PlanComparisonFeatureGate.ObserveBusinessFilters,
  'analytics_product.filters.error': PlanComparisonFeatureGate.FilterError,

  // Ask filters
  'feedback_product.filters.device': PlanComparisonFeatureGate.AskAdvancedFilters,
  'feedback_product.filters.country': PlanComparisonFeatureGate.AskAdvancedFilters,
  'feedback_product.filters.menu_footer': PlanComparisonFeatureGate.AskAdvancedFilters,
  'survey.filters.country': PlanComparisonFeatureGate.AskAdvancedFilters,
  'survey.filters.device': PlanComparisonFeatureGate.AskAdvancedFilters,
  'survey.filters.user_attribute': PlanComparisonFeatureGate.AskAdvancedFilters,
  'survey.filters.menu_footer': PlanComparisonFeatureGate.AskAdvancedFilters,

  // Recordings segments (Plus)
  'recordings.segments.filters.browser': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.country': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.device': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.events': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.exit_url': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.favorite': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.landing_url': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.new_returning_user':
    PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.os': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.page_count': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.session_duration': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.tag': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.team_comment': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.user_id': PlanComparisonFeatureGate.FilterUserSegmentsPlus,
  'recordings.segments.filters.visited_url': PlanComparisonFeatureGate.FilterUserSegmentsPlus,

  // Recordings segments (Business)
  'recordings.segments.filters.clicked': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.google_optimize':
    PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.optimizely': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.unbounce': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.incoming_feedback':
    PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.nps': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.rage_click': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.referrer_url': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.u_turn': PlanComparisonFeatureGate.FilterUserSegmentsBusiness,
  'recordings.segments.filters.user_attribute':
    PlanComparisonFeatureGate.FilterUserSegmentsBusiness,

  // Recordings segments (Scale)
  'recordings.segments.filters.error': PlanComparisonFeatureGate.FilterUserSegmentsScale,
};

export const TrafficRecommendationFeatureGateByReferrer: Record<
  TrafficRecommendationReferrer,
  TrafficRecommendationFeatureGate
> = {
  'sampling-widget.tracking-coverage-message.sumup.available-plans':
    TrafficRecommendationFeatureGate.Traffic,
  'sampling.widget_active-traffic-coverage-rate_upgrade-to-increase-limit':
    TrafficRecommendationFeatureGate.Traffic,
  'sampling-widget.indicator-direct-upsell': TrafficRecommendationFeatureGate.Traffic,
  'recordings.continuous-capture.increase-daily-limit-filtering-no-matches':
    TrafficRecommendationFeatureGate.Traffic,
  'site.settings-session_capture_toggle-increase.daily.limit':
    TrafficRecommendationFeatureGate.Traffic,
  'widget-empty-state': TrafficRecommendationFeatureGate.Traffic,
  dashboard_top_banner_upgrade_link: TrafficRecommendationFeatureGate.Traffic,
  funnels_top_banner_upgrade_link: TrafficRecommendationFeatureGate.Traffic,
  recordings_guide_card: TrafficRecommendationFeatureGate.Traffic,
  recordings_upsell_banner: TrafficRecommendationFeatureGate.Traffic,
  trends_top_banner_upgrade_link: TrafficRecommendationFeatureGate.Traffic,
  'heatmaps.traffic-indicator': TrafficRecommendationFeatureGate.Traffic,
  'heatmaps.no-data': TrafficRecommendationFeatureGate.Traffic,
  'campaign_traffic-coverage-modal': TrafficRecommendationFeatureGate.Traffic,
  'overview.upsell_banner.observe': TrafficRecommendationFeatureGate.Traffic,
};

/**
 * Feature gate details mapped to feature gate keys. Although we could scrap this object and
 * just match the upsell referrers to feature gate details directly, it would result in heavy
 * code duplication, because there are some feature gates that are mapped to multiple referrers.
 *
 * Note: You probably don't need to access this object in component-code. Please see the
 * `usePlanComparisonFeatureGate` hook.
 */
export const planComparisonFeatureGateDetails: Record<
  PlanComparisonFeatureGate,
  PlanComparisonFeatureGateDetails
> = {
  [PlanComparisonFeatureGate.Trends]: {
    title: 'feature-gates.trends.title',
    description: 'feature-gates.trends.description',
    illustration: TrendsSplashIllustration,
    minimumFeatureGroup: SCALE,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.Funnels]: {
    title: 'feature-gates.funnels.title',
    description: 'feature-gates.funnels.description',
    illustration: FunnelsSplashIllustration,
    minimumFeatureGroup: SCALE,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.CustomReactionScales]: {
    title: 'feature-gates.custom-reaction-scales.title',
    description: 'feature-gates.custom-reaction-scales.description',
    illustration: CustomReactionScalesIllustration,
    featureName: 'feedback.emotion_style_choices',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.ObserveEventsApi]: {
    title: 'feature-gates.observe-events-api.title',
    description: 'feature-gates.observe-events-api.description',
    illustration: FiltersIllustration,
    minimumFeatureGroup: PLUS,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.AskEventsApi]: {
    title: 'feature-gates.ask-events-api.title',
    description: 'feature-gates.ask-events-api.description',
    illustration: FiltersIllustration,
    minimumFeatureGroup: PLUS,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.FilterIncomingFeedback]: {
    title: 'feature-gates.filter-incoming-feedback.title',
    description: 'feature-gates.filter-incoming-feedback.description',
    illustration: IncomingFeedbackIllustration,
    featureName: 'filters.incoming_feedback',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterNps]: {
    title: 'feature-gates.filter-nps.title',
    description: 'feature-gates.filter-nps.description',
    illustration: FilterNpsIllustration,
    featureName: 'filters.nps',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterRageClicks]: {
    title: 'feature-gates.filter-rage-clicks.title',
    description: 'feature-gates.filter-rage-clicks.description',
    illustration: PageTargetingIllustration,
    featureName: 'filters.rage_click',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterReferrerUrl]: {
    title: 'feature-gates.filter-referrer-url.title',
    description: 'feature-gates.filter-referrer-url.description',
    illustration: FiltersIllustration,
    featureName: 'filters.referrer_url',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterUserAttributes]: {
    title: 'feature-gates.filter-user-attributes.title',
    description: 'feature-gates.filter-user-attributes.description',
    illustration: FiltersIllustration,
    featureName: 'filters.user_attribute',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterUTurn]: {
    title: 'feature-gates.filter-u-turn.title',
    description: 'feature-gates.filter-u-turn.description',
    illustration: FilterUTurnIllustration,
    featureName: 'filters.u_turn',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterClicked]: {
    title: 'feature-gates.filter-clicked.title',
    description: 'feature-gates.filter-clicked.description',
    illustration: FiltersIllustration,
    featureName: 'filters.clicked',
    minimumFeatureGroup: BUSINESS,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.FilterError]: {
    title: 'feature-gates.filter-error.title',
    description: 'feature-gates.filter-error.description',
    illustration: FilterErrorIllustration,
    minimumFeatureGroup: SCALE,
    sku: ProductType.Analytics,
  },

  [PlanComparisonFeatureGate.IncomingFeedback]: {
    title: 'feature-gates.incoming-feedback.title',
    description: 'feature-gates.incoming-feedback.description',
    illustration: IncomingFeedbackIllustration,
    minimumFeatureGroup: PLUS,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.PageTargeting]: {
    title: 'feature-gates.page-targeting.title',
    description: 'feature-gates.page-targeting.description',
    illustration: PageTargetingIllustration,
    featureName: 'playbacks.page_targeting',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.PercentageTargeting]: {
    title: 'feature-gates.percentage-targeting.title',
    description: 'feature-gates.percentage-targeting.description',
    illustration: PercentageTargetingIllustration,
    featureName: 'targeting.cap',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.RemoveHotjarBranding]: {
    title: 'feature-gates.remove-hotjar-branding.title',
    description: 'feature-gates.remove-hotjar-branding.description',
    illustration: RemoveBrandingIllustration,
    featureName: 'branding.toggle',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.SurveyCustomization]: {
    title: 'feature-gates.survey-customization.title',
    description: 'feature-gates.survey-customization.description',
    illustration: SurveyCustomizationIllustration,
    featureName: 'survey.appearance.colors',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.SurveyPerformance]: {
    title: 'feature-gates.survey-performance.title',
    description: 'feature-gates.survey-performance.description',
    illustration: SurveysIllustration,
    minimumFeatureGroup: BUSINESS,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.Surveys]: {
    title: 'feature-gates.surveys.title',
    description: 'feature-gates.surveys.description',
    illustration: SurveysIllustration,
    minimumFeatureGroup: PLUS,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.UserAttributes]: {
    title: 'feature-gates.user-attributes.title',
    description: 'feature-gates.user-attributes.description',
    illustration: FiltersIllustration,
    featureName: 'user_attributes.toggle',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.AskAdvancedFilters]: {
    title: 'feature-gates.ask-advanced-filters.title',
    description: 'feature-gates.ask-advanced-filters.description',
    illustration: FiltersIllustration,
    minimumFeatureGroup: PLUS,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.ObserveAdvancedFilters]: {
    title: 'feature-gates.observe-advanced-filters.title',
    description: 'feature-gates.observe-advanced-filters.description',
    illustration: FiltersIllustration,
    minimumFeatureGroup: PLUS,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.ObserveBusinessFilters]: {
    title: 'feature-gates.observe-business-filters.title',
    description: 'feature-gates.observe-business-filters.description',
    illustration: FiltersIllustration,
    minimumFeatureGroup: BUSINESS,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.ObserveSlack]: {
    title: 'feature-gates.observe-slack.title',
    description: 'feature-gates.observe-slack.description',
    illustration: ObserveSlackIllustration,
    featureName: 'settings.integrations_slack',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.ObserveMsTeams]: {
    title: 'feature-gates.ask-ms-teams.title',
    description: 'feature-gates.ask-ms-teams.description',
    illustration: ObserveMsTeamsIllustration,
    featureName: 'settings.integrations_teams',
    minimumFeatureGroup: BUSINESS,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.ObserveWebhooks]: {
    title: 'feature-gates.ask-webhooks.title',
    description: 'feature-gates.ask-webhooks.description',
    illustration: WebhooksIllustration,
    featureName: 'settings.integrations_webhooks',
    minimumFeatureGroup: SCALE,
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.Optimizely]: {
    title: 'feature-gates.optimizely.title',
    description: 'feature-gates.optimizely.description',
    illustration: ObserveOptimizelyIllustration,
    featureName: 'settings.integrations_optimizely',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.ObserveZapier]: {
    title: 'feature-gates.observe-zapier.title',
    description: 'feature-gates.observe-zapier.description',
    illustration: ObserveZapierIllustration,
    featureName: 'settings.integrations_zapier',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.AskZapier]: {
    title: 'feature-gates.ask-zapier.title',
    description: 'feature-gates.ask-zapier.description',
    illustration: ObserveZapierIllustration,
    featureName: 'settings.integrations_zapier',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.GoogleOptimize]: {
    title: 'feature-gates.google-optimize.title',
    description: 'feature-gates.google-optimize.description',
    illustration: ObserveGoogleOptimizeIllustration,
    featureName: 'settings.integrations_google_optimize',
    sku: ProductType.Analytics,
  },
  [PlanComparisonFeatureGate.Unbounce]: {
    title: 'feature-gates.unbounce.title',
    description: 'feature-gates.unbounce.description',
    illustration: UnbounceIllustration,
    featureName: 'settings.integrations_unbounce',
    sku: ProductType.Analytics,
    minimumFeatureGroup: BUSINESS,
  },
  [PlanComparisonFeatureGate.AskSlack]: {
    title: 'feature-gates.ask-slack.title',
    description: 'feature-gates.ask-slack.description',
    illustration: ObserveSlackIllustration,
    featureName: 'settings.integrations_slack',
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.AskMsTeams]: {
    title: 'feature-gates.ask-ms-teams.title',
    description: 'feature-gates.ask-ms-teams.description',
    illustration: ObserveMsTeamsIllustration,
    featureName: 'settings.integrations_teams',
    minimumFeatureGroup: BUSINESS,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.AskWebhooks]: {
    title: 'feature-gates.ask-webhooks.title',
    description: 'feature-gates.ask-webhooks.description',
    illustration: WebhooksIllustration,
    featureName: 'settings.integrations_webhooks',
    minimumFeatureGroup: SCALE,
    sku: ProductType.Feedback,
  },
  [PlanComparisonFeatureGate.FilterUserSegmentsPlus]: {
    title: 'feature-gates.user-segments.title',
    description: 'feature-gates.user-segments.description',
    illustration: PageTargetingIllustration,
    sku: ProductType.Analytics,
    minimumFeatureGroup: PLUS,
  },
  [PlanComparisonFeatureGate.FilterUserSegmentsBusiness]: {
    title: 'feature-gates.user-segments.title',
    description: 'feature-gates.user-segments.description',
    illustration: PageTargetingIllustration,
    sku: ProductType.Analytics,
    minimumFeatureGroup: BUSINESS,
  },
  [PlanComparisonFeatureGate.FilterUserSegmentsScale]: {
    title: 'feature-gates.user-segments.title',
    description: 'feature-gates.user-segments.description',
    illustration: PageTargetingIllustration,
    sku: ProductType.Analytics,
    minimumFeatureGroup: SCALE,
  },
  [PlanComparisonFeatureGate.AskResponseLimit]: {
    modalControls: {
      titleKey: 'title-ask-response-limit',
    },
    title: 'feature-gates.ask-response-limit.title',
    description: 'feature-gates.ask-response-limit.description',
    illustration: IncomingFeedbackIllustration,
    sku: ProductType.Feedback,
    isPlanAgnostic: true,
  },
  [PlanComparisonFeatureGate.SurveyQuestionImages]: {
    title: 'feature-gates.survey-question-images.title',
    description: 'feature-gates.survey-question-images.description',
    illustration: SurveyQuestionImagesIllustration,
    sku: ProductType.Feedback,
    minimumFeatureGroup: BUSINESS,
  },
  [PlanComparisonFeatureGate.SurveyAppearanceLogo]: {
    title: 'feature-gates.survey-appearance-logo.title',
    description: 'feature-gates.survey-appearance-logo.description',
    illustration: SurveyAppearanceLogoIllustration,
    sku: ProductType.Feedback,
    minimumFeatureGroup: BUSINESS,
  },
  [PlanComparisonFeatureGate.PublicAPI]: {
    title: 'feature-gates.public-api.title',
    description: 'feature-gates.public-api.description',
    illustration: PublicApiIllustration,
    sku: ProductType.Feedback,
    minimumFeatureGroup: SCALE,
  },
  [PlanComparisonFeatureGate.ErrorConsole]: {
    title: 'feature-gates.error-console.title',
    description: 'feature-gates.error-console.description',
    illustration: FilterErrorIllustration,
    sku: ProductType.Analytics,
    minimumFeatureGroup: SCALE,
  },
};

export const trafficRecommendationFeatureGateDetails: Record<
  TrafficRecommendationFeatureGate,
  PlanComparisonFeatureGateDetails
> = {
  [TrafficRecommendationFeatureGate.Traffic]: {
    modalControls: {
      titleKey: 'title-traffic',
      hideFeatureBarPlans: true,
    },
    title: 'feature-gates.traffic.title',
    description: 'feature-gates.traffic.description',
    illustration: TrafficIllustration,
    sku: ProductType.Analytics,
    staticRecommendedFeatureGroup: BUSINESS,
  },
};

export const planComparisonReferrers = [
  // Observe
  'settings.integrations_google_optimize',
  'settings.integrations_optimizely',
  'settings.integrations_slack',
  'settings.integrations_teams',
  'settings.integrations_webhooks',
  'analytics_product.integrations_slack',
  'analytics_product.integrations_zapier',
  'analytics_product.integrations_teams',
  'analytics_product.integrations_webhooks',
  'site.settings_targeting',
  'site.console_tracking',
  'funnels_splash_screen_upgrade_link',
  'trends_splash_screen_upgrade_link',
  'recordings.heatmaps_error_widget.empty',
  'recordings.error_console.empty',
  'recordings.session_info.error_widget.empty',
  'recordings.recommended_error_widget.empty',

  // Ask
  'settings.integrations_zapier',
  'feedback.appearance.accent_color',
  'feedback.appearance.widget_label',
  'feedback.branding.toggle',
  'feedback.emotion_style_choices',
  'feedback.limit.reached',
  'feedback.targeting.attributes',
  'feedback.targeting.cap',
  'feedback.targeting.triggers',
  'polls.branding.toggle',
  'polls.go-unlimited',
  'polls.limit.reached',
  'polls.targeting.attributes',
  'polls.targeting.cap',
  'polls.targeting.triggers',
  'survey.appearance.colors',
  'survey.appearance.logo',
  'survey.emotion_style_choices',
  'surveys.performance',
  'survey.question.images',
  'user-attributes.upgrade',
  'feedback_product.integrations_slack',
  'feedback_product.integrations_teams',
  'feedback_product.integrations_zapier',
  'feedback_product.integrations_webhooks',
  'overview.upsell_banner.ask',
  // Ask scale
  'public_api',

  // Observe filters
  'analytics_product.filters.google_optimize',
  'analytics_product.filters.optimizely',
  'analytics_product.filters.unbounce',
  'analytics_product.filters.incoming_feedback',
  'analytics_product.filters.nps',
  'analytics_product.filters.referrer_url',
  'analytics_product.filters.rage_click',
  'analytics_product.filters.user_attribute',
  'analytics_product.filters.u_turn',
  'analytics_product.filters.favorite',
  'analytics_product.filters.visited_url',
  'analytics_product.filters.landing_url',
  'analytics_product.filters.exit_url',
  'analytics_product.filters.new_returning_user',
  'analytics_product.filters.clicked',
  'analytics_product.filters.country',
  'analytics_product.filters.session_duration',
  'analytics_product.filters.page_count',
  'analytics_product.filters.tag',
  'analytics_product.filters.events',
  'feedback_product.response_limit_50_percent',
  'feedback_product.response_limit_100_percent',
  'analytics_product.filters.user_id',
  'analytics_product.filters.team_comment',
  'analytics_product.filters.device',
  'analytics_product.filters.browser',
  'analytics_product.filters.os',
  'analytics_product.filters.menu_footer',
  'analytics_product.filters.error',

  // Ask filters
  'feedback_product.filters.device',
  'feedback_product.filters.country',
  'feedback_product.filters.menu_footer',
  'survey.filters.country',
  'survey.filters.device',
  'survey.filters.user_attribute',
  'survey.filters.menu_footer',

  // Recordings segments (Plus)
  'recordings.segments.filters.browser',
  'recordings.segments.filters.country',
  'recordings.segments.filters.device',
  'recordings.segments.filters.events',
  'recordings.segments.filters.exit_url',
  'recordings.segments.filters.favorite',
  'recordings.segments.filters.landing_url',
  'recordings.segments.filters.new_returning_user',
  'recordings.segments.filters.os',
  'recordings.segments.filters.page_count',
  'recordings.segments.filters.session_duration',
  'recordings.segments.filters.tag',
  'recordings.segments.filters.team_comment',
  'recordings.segments.filters.user_id',
  'recordings.segments.filters.visited_url',

  // Recordings segments (Business)
  'recordings.segments.filters.clicked',
  'recordings.segments.filters.google_optimize',
  'recordings.segments.filters.optimizely',
  'recordings.segments.filters.unbounce',
  'recordings.segments.filters.incoming_feedback',
  'recordings.segments.filters.nps',
  'recordings.segments.filters.rage_click',
  'recordings.segments.filters.referrer_url',
  'recordings.segments.filters.u_turn',
  'recordings.segments.filters.user_attribute',

  // Recordings segments (Scale)
  'recordings.segments.filters.error',
] as const;

export const trafficRecommendationReferrers = [
  'sampling-widget.tracking-coverage-message.sumup.available-plans',
  'sampling.widget_active-traffic-coverage-rate_upgrade-to-increase-limit',
  'sampling-widget.indicator-direct-upsell',
  'recordings.continuous-capture.increase-daily-limit-filtering-no-matches',
  'site.settings-session_capture_toggle-increase.daily.limit',
  'widget-empty-state',
  'dashboard_top_banner_upgrade_link',
  'funnels_top_banner_upgrade_link',
  'recordings_guide_card',
  'recordings_upsell_banner',
  'trends_top_banner_upgrade_link',
  'heatmaps.traffic-indicator',
  'heatmaps.no-data',
  'campaign_traffic-coverage-modal',
  'overview.upsell_banner.observe',
] as const;

// todo: maybe use SITE_BAR_LINK instead of 'navigation.top-nav.upgrade-link'
export const multiProductsReferrers = [
  'navigation.top-nav.upgrade-link',
  'overview.upsell_banner.generic',
] as const;

export const plansAndBillingRedirectionReferrers = [
  SITE_BAR_LINK,
  SUBSCRIPTION_COST_SUMMARY_BUSINESS,
  'heatmaps.limit.reached',
  'heatmaps.go-unlimited',
  'heatmaps-go-unlimited',
  'heatmaps.create.downgraded',
  'heatmaps.list.downgraded',
  'heatmaps.snapshot-size-2000',
  'heatmaps.snapshot-size-10000',
  'heatmaps-sample-size',
  'Space Guide Card',
] as const;

export const knownReferrers = [
  ...planComparisonReferrers,
  ...trafficRecommendationReferrers,
  ...plansAndBillingRedirectionReferrers,
  ...multiProductsReferrers,
] as const;
